import _ from 'underscore';
import { Scope } from '@bingads-webui-react/with-site-map';
import { externalMap } from './external-site-config';

export function searchSiteMap(siteMap, siteMapSearch) {
  if (!siteMap || _.isEmpty(siteMap)) {
    return false;
  }

  let site = false;
  _.find(siteMap, (parent) => {
    if (parent.children) {
      const child = _.findWhere(parent.children, siteMapSearch);

      if (child) {
        // The parent has the scope properties, but the child has the route, combine them.
        site = _.extend({}, parent, child);
        return true;
      }
    }

    if (_.isMatch(parent, siteMapSearch)) {
      site = parent;
      return true;
    }

    return false;
  });

  if (site) {
    return site;
  }

  return searchSiteMap(_.chain(siteMap)
    .pluck('children')
    .reject(s => !s)
    .flatten()
    .value(), siteMapSearch);
}

function getCampaignHashRoute(currentScopeIds, allowedScopes) {
  // We want to find the narrowest scope possible that is currently selected and
  // allowed by the site map. Start at ad and work our way up.
  const components = [];
  if (currentScopeIds.adId && allowedScopes.includes(Scope.Ad)) {
    components.unshift(`ad/${currentScopeIds.adId}/`);
  }

  // We want to push further scopes if
  // 1) we already added a scope
  // 2) we have an allowed scope
  if (components.length > 0
    || (currentScopeIds.adGroupId && allowedScopes.includes(Scope.AdGroup))) {
    components.unshift(`adgroup/${currentScopeIds.adGroupId}/`);
  }

  if (components.length > 0
    || (currentScopeIds.campaignId && allowedScopes.includes(Scope.Campaign))) {
    components.unshift(`campaign/${currentScopeIds.campaignId}/`);
  }

  if (currentScopeIds.ptId && allowedScopes.includes(Scope.PerformanceTarget)) {
    components.unshift(`performance_target/${currentScopeIds.ptId}/`);
  }

  if (components.length > 0
    || (currentScopeIds.accountId && allowedScopes.includes(Scope.Account))) {
    components.unshift(`account/${currentScopeIds.accountId}/`);
  }

  // Always add customer id
  if (currentScopeIds.customerId) {
    components.unshift(`customer/${currentScopeIds.customerId}/`);
  }

  return `#${components.join('')}`;
}

function getParams(scopeIds, includeAccount = true, site = null) {
  if (!scopeIds) {
    return '';
  }

  const params = [];
  if (scopeIds.customerId) {
    params.push(`cid=${scopeIds.customerId}`);
  }

  if (scopeIds.accountId && includeAccount) {
    params.push(`aid=${scopeIds.accountId}`);
  }

  if (scopeIds.userId) {
    params.push(`uid=${scopeIds.userId}`);
  }

  if (site && site.route === 'adpreview') {
    if (scopeIds.campaignId) {
      params.push(`campaignId=${scopeIds.campaignId}`);
    }

    if (scopeIds.adGroupId) {
      params.push(`adgroupId=${scopeIds.adGroupId}`);
    }

    if (scopeIds.keywordId) {
      params.push(`keywordId=${scopeIds.keywordId}`);
    }
  }

  return `?${params.join('&')}`;
}

function getPath(scope, replacementPath) {
  const regEx = new RegExp('/campaign/vnext', 'ig');

  const result = scope.location.pathname.replace(regEx, replacementPath);

  const substringIndex = result.indexOf(replacementPath);
  if (substringIndex > -1) {
    return result.substring(0, substringIndex + replacementPath.length);
  }

  return result;
}

function getCampaignStylePath({
  scope,
  siteMap,
  siteMapSearch,
  basePath,
}) {
  const base = getPath(scope, basePath);

  const scopeIds = scope.values && {
    customerId: scope.values.cid,
    accountId: scope.values.aid,
    campaignId: scope.values.campaignId,
    adGroupId: scope.values.adGroupId,
    adId: scope.values.adId,
    ptId: scope.values.ptId,
    userId: scope.values.uid,
  };

  const params = getParams(scopeIds);

  let hashRoute = '';
  if (siteMapSearch && (siteMapSearch.id || siteMapSearch.route)) {
    let site = false;
    if (siteMapSearch.id) {
      site = searchSiteMap(siteMap, { id: siteMapSearch.id });
    } else {
      site = searchSiteMap(siteMap, { route: siteMapSearch.route });
    }

    // Sometimes the parent and a child have the same id, but only the child has a route.
    // If this the case, we should use the child. Experiments is an example in the site map.
    if (site && !site.oldUIRoute && site.children) {
      const childSite = _.findWhere(site.children, siteMapSearch);
      if (childSite) {
        site = childSite;
      }
    }

    const oldUIRoute = site && site.oldUIRoute;

    if (oldUIRoute) {
      let allowedScopes = site.oldUIScope || site.scope;
      if (!allowedScopes) {
        // In site map if no scope is set it is all scopes
        allowedScopes = [Scope.Account, Scope.Campaign, Scope.AdGroup];
      }

      hashRoute = getCampaignHashRoute(scopeIds, allowedScopes).concat(oldUIRoute);
    }
  }

  return base + params + hashRoute;
}

function getPathFromCampaignSiteMap({ scope, siteMap, siteMapSearch }) {
  return getCampaignStylePath({
    scope,
    siteMap,
    siteMapSearch,
    basePath: '/Campaign/Campaigns',
  });
}

function getAccountsSummaryPath({ scope, siteMap, siteMapSearch }) {
  return getCampaignStylePath({
    scope,
    siteMap,
    siteMapSearch,
    basePath: '/Campaign/AccountsSummary',
  });
}

function getPathFromExternal({ scope, site }) {
  const base = getPath(scope, site.oldUIBaseRoute);

  const scopeIds = scope.values && {
    customerId: scope.values.cid,
    accountId: scope.values.aid,
    campaignId: scope.values.campaignId,
    adGroupId: scope.values.adGroupId,
    keywordId: scope.values.keywordId,
    adId: scope.values.adId,
    userId: scope.values.uid,
  };

  const includeAccount = site.scope && site.scope.includes(Scope.Account);
  const params = getParams(scopeIds, includeAccount, site);

  let hashRoute = '';
  if (site.oldUIHashRoute) {
    hashRoute = `#${site.oldUIHashRoute}`;
  }

  if (site.source === 'cc' && hashRoute.length > 0) {
    // CC adds the params again in the hash route
    hashRoute += params;
  }

  if (site.source === 'smart' && scopeIds.campaignId) {
    // CC adds the params again in the hash route
    hashRoute += `Campaign/${scopeIds.campaignId}`;
  }

  return base + params + hashRoute;
}

export const getFallbackPath = (scope, siteMap, siteMapSearch) => {
  const externalSite = siteMapSearch && _.findWhere(externalMap, siteMapSearch);
  if (externalSite) {
    switch (externalSite.source) {
      // Import is not in the campaign site-map currently but has one Campaign/Campaigns link
      // in old UI, use this as a workaround
      case 'campaign':
        return getPathFromCampaignSiteMap({ scope, siteMap: externalMap, siteMapSearch });
      case 'accounts-summary':
        return getAccountsSummaryPath({ scope, siteMap: externalMap, siteMapSearch });
      default:
        return getPathFromExternal({ scope, site: externalSite });
    }
  }
  return getPathFromCampaignSiteMap({ scope, siteMap, siteMapSearch });
};

export const getCurrentRoute = (scope) => {
  const regEx = new RegExp('campaign/vnext/([\\w/]+)', 'i');

  const result = regEx.exec(scope.location.pathname);

  return result && result.pop();
};

